import React, { useEffect, useMemo, useRef, useState, Suspense } from "react";
import { EItemsDiffType, EPageType, IDataItem, ILeftList, ISearchModel, ITransformedData } from "@Types/specification";
import { useMount, useThrottleEffect, useThrottleFn } from "ahooks";
import { Spin } from "antd";
import styled from "styled-components";

const SearchModel = React.lazy(() => import("@Components/Specification/searchModel"));
const TableSpecification = React.lazy(() => import("./table"));
const CompareRec = React.lazy(() => import("./compareRec"));

const SpinStyled = styled(Spin)`
  .ant-spin-dot-item {
    background-color: #3264ff;
  }
  .ant-spin-text {
    margin-top: 10px;
  }
`;

const LoadingSpinner = () => (
  <SpinStyled
    size='large'
    className='w-[1200px] h-full mt-[300px]'
    tip={<span className='text-14 text-[#3264FF] pt-20'>載入中...</span>}
  />
);


interface IProps {
  fetchData: IDataItem[];
  transformedData: ITransformedData;
  filteredTransformedData: ITransformedData;
  pageType: EPageType;
  setPinAdsGetData: (params: any) => void;
  getData: (params: any) => void;
  setTransformedData: (data: ITransformedData) => void;
  setFilteredTransformedData: (data: ITransformedData) => void;
}

const Specification: React.FC<IProps> = ({
  fetchData,
  transformedData,
  filteredTransformedData,
  pageType,
  getData,
  setTransformedData,
  setFilteredTransformedData,
  setPinAdsGetData,
}) => {
  const searchRef = useRef({});
  const [searchValue, setSearchValue] = useState<string>("");
  const [searchData, setSearchData] = useState<ISearchModel[]>([]);
  const [saveFilterParams, setSaveSaveFilterParams] = useState<{ diffItems?: EItemsDiffType[] }>({
    diffItems: [EItemsDiffType.HIDE_NONE],
  });
  const [sectionChange, setSectionChange] = useState<number>(0);
  const [leftList, setLeftList] = useState<ILeftList[]>([]);
  const isFirstModelRef = useRef({
    firstId: null,
    isFirstGetId: true,
  });

  // 过滤非广告车型
  const filterAdsIds = useMemo(() => {
    const modelYInfo = transformedData.modelYearInfo || [];
    return new Set(modelYInfo.filter((item) => !item.isAds).map((item) => item.modelYearId));
  }, [transformedData]);

  // 搜索
  useThrottleEffect(
    () => {
      if (!!searchValue && !!filteredTransformedData.equipSpec.length) {
        const newData: any[] = filteredTransformedData.equipSpec
          .map((item) => {
            const filteredItems = item.items
              .filter((i) => i.name.includes(searchValue))
              .map((i) => ({ name: i.name, entryId: i.entryId }));
            return filteredItems.length > 0 ? filteredItems : false;
          })
          .filter(Boolean);

        const flattenedArray = newData.reduce((acc, curr) => acc.concat(curr), []);

        setSearchData([...flattenedArray]);
      }
    },
    [searchValue],
    { wait: 1000 },
  );

  // 搜索点击
  const handleClickSearch = (entryId: number) => {
    if (searchRef.current) {
      (searchRef.current as any).scrollToItem(entryId);
    }
  };

  // 左侧栏点击
  const handleLeftClick = (params) => {
    setSectionChange(params);
  };

  // 高亮不同、隐藏相同、隐藏暂无 筛选后过滤总数据
  const updateFilterCheckbox = (params: { diffItems?: EItemsDiffType[] }) => {
    setSaveSaveFilterParams(params);

    const newFormedData = JSON.parse(JSON.stringify(transformedData));
    const newDataModelYearInfo = newFormedData.modelYearInfo;
    const newEquipSpec = newFormedData.equipSpec;

    // 只剩一条车型数据，高亮不同、隐藏相同 置灰并清除选中
    if (!!newDataModelYearInfo.length && newDataModelYearInfo.length === 1) {
      params.diffItems = params.diffItems!.includes(EItemsDiffType.HIDE_NONE) ? [EItemsDiffType.HIDE_NONE] : [];
    }

    // 规格配备 - 高亮不同、隐藏相同、隐藏暂无
    const result =
      !!(newEquipSpec || []).length &&
      !!(params.diffItems || []).length &&
      pageType === EPageType.SPEC &&
      newEquipSpec.map((spec) => ({
        ...spec,
        items: spec.items
          .map((item) => {
            const seenValues = new Set();
            const filteredItemss = item.itemss.filter((entry) => {
              if (!seenValues.has(entry.value)) {
                seenValues.add(entry.value);
                return true;
              }
              return false;
            });

            // 隱藏相同項
            if (params.diffItems!.includes(EItemsDiffType.HIDE_IDENTICAL) && filteredItemss.length === 1) return null;

            // 隱藏暫無項
            if (
              params.diffItems!.includes(EItemsDiffType.HIDE_NONE) &&
              filteredItemss.length === 1 &&
              filteredItemss[0].value === ""
            )
              return null;

            return {
              ...item,
              itemss: item.itemss,
            };
          })
          .filter((item) => item !== null),
      }));

    // 综合比较 - 隐藏 噪音、震動 暂无
    const resultSummary =
      !!(newEquipSpec || []).length &&
      pageType === EPageType.SUMMARY &&
      newEquipSpec.map((spec) => ({
        ...spec,
        items: spec.items
          .map((item) => {
            const seenValues = new Set();
            const filteItemss = item.itemss.filter((entry) => {
              if (!seenValues.has(entry.value) && (spec.name === "噪音" || spec.name === "震動")) {
                seenValues.add(entry.value);
                return true;
              }
              return false;
            });

            if (filteItemss.length === 1 && filteItemss[0].value === "") return null;

            return {
              ...item,
              itemss: item.itemss,
            };
          })
          .filter((item) => item !== null),
      }));

    setFilteredTransformedData({
      modelYearInfo: newDataModelYearInfo,
      equipSpec:
        !!(params.diffItems || []).length && pageType === EPageType.SPEC
          ? result
          : pageType === EPageType.SUMMARY
            ? resultSummary
            : newEquipSpec,
    });
  };

  // 滚动监听置顶交互
  const { run: debouncedHandleScroll } = useThrottleFn(
    () => {
      const scrollTop = window.scrollY;
      if (scrollTop === 0 && leftList.length > 0) {
        setSectionChange(leftList[0].id);
      }
    },
    { wait: 30 },
  );

  useEffect(() => {
    const handleScroll = () => {
      debouncedHandleScroll();
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [debouncedHandleScroll]);

  useEffect(() => {
    if (fetchData.length > 0) {
      setSaveSaveFilterParams({ diffItems: [EItemsDiffType.HIDE_NONE] });
    }
  }, [fetchData]);

  useEffect(() => {
    if (transformedData.equipSpec.length > 0) {
      updateFilterCheckbox({ ...saveFilterParams });
    }
  }, [transformedData]);

  useEffect(() => {
    if (filteredTransformedData.equipSpec.length > 0) {
      const newLeftList = filteredTransformedData.equipSpec
        .map((item) => {
          // 震動、噪音 属于空間舒適比較的子级，硬编码处理
          if (item.name === "震動" || item.name === "噪音") {
            const spaceComfortCompareItem = filteredTransformedData.equipSpec.find((e) => e.name === "空間舒適比較");
            if (spaceComfortCompareItem) {
              return {
                id: spaceComfortCompareItem.id,
                name: "空間舒適比較",
                items: spaceComfortCompareItem.items,
              };
            }
          }
          return {
            id: item.id,
            name: item.name,
            items: item.items,
          };
        })
        .filter((item, index, self) => {
          return item.items.length > 0 && self.findIndex((i) => i.name === item.name && i.id === item.id) === index;
        })
        .filter((item) => item.items.length > 0);

      setLeftList(newLeftList);

      handleLeftClick(filteredTransformedData.equipSpec[0].id);
    }
  }, [filteredTransformedData]);

  return (
    <div className='flex'>
      {/* 左侧栏 */}
      <div className='w-180 min-w-[180px] max-w-[180px] sticky left-0 max-w-[134px] min-w-[134px] bg-[#F8F9FC] pl-28 pr-18 z-[9]'>
        <div className='sticky top-0 pt-8 left-0 '>
          {pageType === EPageType.SPEC && (
            <div className='w-134 flex items-center justify-between rounded-4 border-1 border-solid border-[#9DA2AF] mb-12 relative cursor-pointer text-14 relative'>
              <input
                id='SearchDom'
                type='text'
                className='w-full h-32 rounded-4 placeholder:text-[#9DA2AF] px-12 text-[#1D2029]'
                placeholder='搜尋規格配備項'
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
              />
              <SearchModel
                sourceData={searchData}
                searchValue={searchValue}
                handleClick={handleClickSearch}
                onClear={() => setSearchValue("")}
              />
            </div>
          )}

          <div id='tracking-leftList' className='w-134 flex flex-col items-start justify-between'>
            {!!leftList.length &&
              leftList.map((item) => (
                <span
                  className={`tracking-Pk${pageType}LeftListItem ${Number(sectionChange) === item.id ? "bg-[#3264FF] text-white" : "text-[#1D2029] hover:bg-[#E0EFFF]"
                    } text-14 w-full h-32 rounded-4 pl-12 flex items-center  cursor-pointer `}
                  key={item.id}
                  onClick={() => {
                    handleLeftClick(item.id);
                    (searchRef.current as any).clickLeftItem(item.id);
                  }}
                >
                  {item.name}
                </span>
              ))}
          </div>
        </div>
      </div>

      {/* 右主内容 */}
      <div className='flex flex-col items-start justify-start flex-1'>
        <section className='bg-[#fff]'>
          {!!filteredTransformedData.equipSpec.length ? (
            <Suspense fallback={<LoadingSpinner />}>
              <TableSpecification
                fetchData={fetchData}
                getData={getData}
                pageType={pageType}
                filteredTransformedData={filteredTransformedData}
                searchRef={searchRef}
                isFirstModelRef={isFirstModelRef}
                setPinAdsGetData={setPinAdsGetData}
                setTransformedData={setTransformedData}
                onSectionChange={(title) => handleLeftClick(title)}
                updateFilterCheckbox={(params) => updateFilterCheckbox({ ...saveFilterParams, diffItems: [...params] })}
              />

              <p className='mt-14 mb-20 text-[#656873] pl-14 sticky left-[180px] w-300'>
                溫馨提示：以上數據僅供參考，車型參配以實車為準
              </p>
            </Suspense>
          ) : (
            <LoadingSpinner />
          )}
        </section>

        {/* 推荐比较 */}
        {pageType === EPageType.SUMMARY && (
          <Suspense fallback={<></>}>
            <CompareRec modelYearIds={filteredTransformedData.modelYearInfo.map((item) => item.modelYearId).join(",")} />
          </Suspense>
        )}
      </div>
    </div>
  );
};

export default Specification;
